import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Text, Icon } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import cn from 'utils/cn';

const DayDataPropType = PropTypes.shape({
  dateISO: PropTypes.string.isRequired,
  price: PropTypes.number,
  departures: PropTypes.number.isRequired,
  isLoadingPrice: PropTypes.bool,
  badge: PropTypes.oneOf(['lowest_price', 'early_bird', 'return', null]),
});

/**
 * Generate a relative date label for display using moment's locale.
 * @param {string} dateISO - ISO date string (YYYY-MM-DD)
 * @param {Function} t - i18n translation function.
 * @returns {string} Relative label (today, tomorrow, or formatted date without weekday)
 */
const getRelativeLabel = (dateISO, t) => {
  const today = moment().startOf('day');
  const target = moment(dateISO, 'YYYY-MM-DD');
  const diffDays = target.diff(today, 'days');

  if (diffDays === 0) return t('day_price_selector.today');
  if (diffDays === 1) return t('day_price_selector.tomorrow');
  return target
    .format('ddd D MMM')
    .replace(/\./g, '')
    .replace(/^\w/, (c) => c.toUpperCase());
};

/**
 * Get CSS classes for day button based on state.
 * @param {boolean} isDisabled - Whether the day is disabled.
 * @param {boolean} isSelected - Whether the day is selected.
 * @returns {string} CSS class string for the button.
 */
const getDayButtonClass = (isDisabled, isSelected) => {
  if (isDisabled) return 'border-gray-200 bg-gray-200 opacity-50 cursor-not-allowed';
  if (isSelected)
    return 'border-primary-regular bg-white shadow-sm shadow-primary-regular border-2 border-solid';
  return 'border-white bg-white hover:border-primary-regular hover:shadow-md';
};

/**
 * DayItem component.
 * @param {object} props - day, isSelected, visibleCount, onClick.
 * @param {object} props.day - Day data object.
 * @param {boolean} props.isSelected - Whether the day is selected.
 * @param {number} props.visibleCount - Number of visible days.
 * @param {function} props.onClick - Click handler function.
 */
const DayItem = memo(({ day, isSelected, visibleCount, onClick }) => {
  const { t } = useTranslation('search');
  const isDisabled = day.departures === 0;
  const dayLabel = getRelativeLabel(day.dateISO, t);

  return (
    <div
      className="fade-slide-left opacity-0 flex-shrink-0"
      style={{ width: `calc((100% - ${(visibleCount - 1) * 0.5}rem) / ${visibleCount})` }}
    >
      <button
        type="button"
        onClick={() => onClick(day)}
        disabled={isDisabled}
        aria-pressed={isSelected}
        className={cn(
          'flex flex-col items-center justify-center w-full h-16 md:h-12 relative p-1.5 rounded-3xl border-2 border-solid transition-[border-color,box-shadow] ease-out duration-150',
          getDayButtonClass(isDisabled, isSelected),
        )}
      >
        <Text
          color={isSelected ? 'primary' : 'grayMedium'}
          weight={isSelected ? 'bold' : 'regular'}
          size="XS"
        >
          {dayLabel}
        </Text>
        {day.price > 0 && (
          <LocalizedCurrency
            price={day.price}
            color={isSelected ? 'primary' : 'grayLight'}
            weight="semibold"
            size="XS"
            currencyStyle="currency"
            showCurrencySuffix={false}
          />
        )}
        {!day.price && day.isLoadingPrice && (
          <span
            aria-hidden="true"
            className="mt-1 h-3 w-12 rounded-full bg-gray-200 animate-pulse"
            data-testid="day-price-skeleton"
          />
        )}
      </button>
    </div>
  );
});

DayItem.propTypes = {
  day: DayDataPropType.isRequired,
  isSelected: PropTypes.bool,
  visibleCount: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

DayItem.defaultProps = {
  isSelected: false,
};

const DESKTOP_VISIBLE_DAYS = 7;
const MOBILE_VISIBLE_DAYS = 3;
const DESKTOP_BREAKPOINT = '(min-width: 768px)';
const GAP_PX = 8; // gap-2 = 0.5rem at 16px base

/**
 * Number of days the carousel shows at the current viewport width.
 * @returns {number} Visible days count.
 */
const getVisibleCount = () =>
  window.matchMedia(DESKTOP_BREAKPOINT).matches ? DESKTOP_VISIBLE_DAYS : MOBILE_VISIBLE_DAYS;

/**
 * DaysCarousel component.
 * @param {object} props - days, onSelect, onScroll, selectedDate, showTodayButton.
 * @param {Array} props.days - Array of day data objects.
 * @param {function} props.onSelect - Callback function when a day is selected.
 * @param {function} props.onScroll - Callback fired when navigating. Receives direction and the newly visible date.
 * @param {string} props.selectedDate - ISO date string of the selected day.
 * @param {boolean} props.showTodayButton - Whether the "Today" shortcut button is allowed to render.
 * @param {string} props.containerClassName - Class name for the container div.
 * @param {function} props.onVisibleRangeChange - Callback fired with the start index and count of
 *   the days on screen, on mount and whenever they change.
 */
const DaysCarousel = ({
  days,
  onSelect,
  onScroll,
  selectedDate,
  showTodayButton,
  containerClassName,
  onVisibleRangeChange,
}) => {
  const { t } = useTranslation('search');
  const containerRef = useRef(null);

  /**
   * Calculate the start index for the carousel given a date ISO string, clamped to the last scrollable slot.
   * @param {string} dateISO - Date in YYYY-MM-DD format.
   * @param {number} count - Number of currently visible days.
   * @returns {number} Start index for the carousel.
   */
  const getStartIndexForDate = useCallback(
    (dateISO, count) => {
      if (!dateISO || !days) return 0;
      const idx = days.findIndex((d) => d.dateISO === dateISO);
      const unclamped = idx > 1 ? idx - 1 : 0;
      return Math.min(unclamped, Math.max(0, days.length - count));
    },
    [days],
  );

  // Always keep a ref to the latest getStartIndexForDate so the startIndex
  // effect can read it without adding it to its own dependency array.
  // This prevents price updates (which rebuild `days` and thus the callback)
  // from inadvertently resetting the scroll position.
  const getStartIndexForDateRef = useRef(getStartIndexForDate);
  getStartIndexForDateRef.current = getStartIndexForDate;

  // Read from the viewport on the first render, so the first visible range reported on mobile is
  // the mobile one.
  const [visibleCount, setVisibleCount] = useState(getVisibleCount);
  const [startIndex, setStartIndex] = useState(
    () => getStartIndexForDate(selectedDate, visibleCount),
    // eslint-disable-next-line function-paren-newline
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT);

    /** Update visible count based on the current viewport width. */
    const handleResize = () => {
      setVisibleCount(getVisibleCount());
    };

    handleResize();
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);

  /**
   * Set the start index for the carousel based on the selected date and visible count.
   * Uses a ref to avoid resetting the scroll position when the selected date changes.
   */
  useEffect(() => {
    setStartIndex(getStartIndexForDateRef.current(selectedDate, visibleCount));
  }, [selectedDate, visibleCount]);

  useEffect(() => {
    if (onVisibleRangeChange) onVisibleRangeChange(startIndex, visibleCount);
  }, [onVisibleRangeChange, startIndex, visibleCount]);

  /**
   * Set the carousel step based on the container width and visible count.
   */
  useEffect(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const itemWidth = (containerWidth - (visibleCount - 1) * GAP_PX) / visibleCount;
    containerRef.current.style.setProperty('--carousel-step', `${itemWidth + GAP_PX}px`);
  }, [visibleCount]);

  const handlePrevious = useCallback(() => {
    const nextStartIndex = Math.max(0, startIndex - 1);
    setStartIndex(nextStartIndex);
    if (onScroll) onScroll('previous', days?.[nextStartIndex]?.dateISO);
  }, [days, onScroll, startIndex]);

  const handleNext = useCallback(() => {
    const nextStartIndex = Math.max(
      0,
      Math.min((days?.length ?? 0) - visibleCount, startIndex + 1),
    );
    const newlyVisibleIndex = Math.min((days?.length ?? 0) - 1, startIndex + visibleCount);
    setStartIndex(nextStartIndex);
    if (onScroll) onScroll('next', days?.[newlyVisibleIndex]?.dateISO);
  }, [days, startIndex, visibleCount, onScroll]);

  const handleDayClick = useCallback(
    (day) => {
      if (day.departures === 0) return;
      if (onSelect) onSelect(day);
    },
    [onSelect],
  );

  const todayISO = moment().format('YYYY-MM-DD');

  const handleGoToToday = useCallback(() => {
    const todayDay = days?.find((d) => d.dateISO === todayISO);
    if (!todayDay) return;
    onSelect?.(todayDay);
    setStartIndex(getStartIndexForDate(todayISO, visibleCount));
  }, [days, todayISO, onSelect, getStartIndexForDate, visibleCount]);

  if (!days || !days.length) return null;

  const canGoPrevious = startIndex > 0;
  const canGoNext = startIndex < days.length - visibleCount;
  const isTodaySelected = selectedDate === todayISO;
  const showGoToToday = showTodayButton && canGoPrevious && !isTodaySelected;

  // Only render items within a buffer window around the current position.
  // A spacer div replaces the non-rendered items to the left so that
  // translateX positions the visible items correctly.
  const RENDER_BUFFER = visibleCount * 2;
  const sliceStart = Math.max(0, startIndex - RENDER_BUFFER);
  const sliceEnd = Math.min(days.length, startIndex + visibleCount + RENDER_BUFFER);
  const windowedDays = days.slice(sliceStart, sliceEnd);

  return (
    <div className={containerClassName}>
      <div className="relative flex items-center w-full min-w-0 mt-8">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          aria-label={t('day_price_selector.previous_day')}
          className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10 items-center justify-center flex rounded-full bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors shadow-lg disabled:shadow-none md:relative md:left-4 z-10"
        >
          <Icon type="ChevronLeft" color="primary" size="S" />
        </button>

        <div
          ref={containerRef}
          className="w-full overflow-hidden shadow-sm border-2 border-white py-1 px-0.5 md:px-2 rounded-3xl"
        >
          <div
            className="flex gap-2 transition-transform duration-500 ease-out"
            style={{ transform: `translateX(calc(-${startIndex} * var(--carousel-step, 0px)))` }}
          >
            {sliceStart > 0 && (
              <div
                aria-hidden="true"
                style={{
                  width: `calc(${sliceStart} * var(--carousel-step, 0px) - ${GAP_PX}px)`,
                  flexShrink: 0,
                }}
              />
            )}
            {windowedDays.map((day) => (
              <DayItem
                key={day.dateISO}
                day={day}
                isSelected={selectedDate === day.dateISO}
                visibleCount={visibleCount}
                onClick={handleDayClick}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label={t('day_price_selector.next_day')}
          className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10 items-center justify-center flex rounded-full bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors shadow-lg disabled:shadow-none md:relative md:right-4 z-10"
        >
          <Icon type="ChevronRight" color="primary" size="S" />
        </button>
        {showGoToToday && (
          <button
            type="button"
            onClick={handleGoToToday}
            className="absolute -top-6 left-12 flex items-center gap-1 text-primary-regular underline "
          >
            <Icon type="Calendar" color="primary" size="XS" />
            <Text color="primary" size="XS">
              {t('day_price_selector.today')}
            </Text>
          </button>
        )}
      </div>
    </div>
  );
};

DaysCarousel.propTypes = {
  days: PropTypes.arrayOf(DayDataPropType),
  onSelect: PropTypes.func,
  onScroll: PropTypes.func,
  selectedDate: PropTypes.string,
  showTodayButton: PropTypes.bool,
  containerClassName: PropTypes.string,
  onVisibleRangeChange: PropTypes.func,
};

DaysCarousel.defaultProps = {
  days: undefined,
  onSelect: undefined,
  onScroll: undefined,
  selectedDate: undefined,
  showTodayButton: false,
  onVisibleRangeChange: undefined,
};

export default DaysCarousel;
